import React from 'react';
import { Theme } from '../Theme';
import './RichTextEditor.css';

type RichTextEditorProps = {
    value: string;
    onChange: (value: string) => void;
    onBlur?: (value: string) => void;
    disabled?: boolean;
    placeholder?: string;
    ariaLabel?: string;
    minHeight?: number | string;
    maxHeight?: number | string;
    unpadded?: boolean;
    editorStyle?: React.CSSProperties;
    toolbarStyle?: React.CSSProperties;
};

const RICH_TEXT_TAG_REGEX = /<\/?[a-z][\s\S]*>/i;
const ENCODED_RICH_TEXT_TAG_REGEX = /&lt;\/?(b|strong|i|em|u|br|div|p|ul|ol|li|a|font|span|h[1-6])(?:\s|&gt;)/i;
const BLOCK_TAGS = new Set(['DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI']);
const DISALLOWED_TAGS = ['script', 'style', 'iframe', 'object', 'embed', 'meta', 'link'];
const ALLOWED_ATTRIBUTES = new Set(['href', 'target', 'rel', 'size', 'color']);

// Escape arbitrary plain text before inserting it into an HTML editor surface.
export function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

// Decode rich text that was previously stored as escaped HTML.
export function decodeHtmlEntities(text: string): string {
    return text
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, '\'');
}

// Detect saved editor HTML without treating ordinary plain text as markup.
export function hasRichTextHtml(text: string): boolean {
    return RICH_TEXT_TAG_REGEX.test(text) || ENCODED_RICH_TEXT_TAG_REGEX.test(text);
}

// Keep links useful while rejecting script-like hrefs.
function isSafeHref(value: string): boolean {
    const trimmedValue = value.trim().toLowerCase();
    return trimmedValue.startsWith('http://') ||
        trimmedValue.startsWith('https://') ||
        trimmedValue.startsWith('mailto:') ||
        trimmedValue.startsWith('tel:') ||
        trimmedValue.startsWith('/') ||
        trimmedValue.startsWith('#');
}

// Strip dangerous tags and attributes from editor HTML before display or save.
export function sanitizeRichTextHtml(html: string): string {
    if (typeof document === 'undefined') {
        return html.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
    }

    const template = document.createElement('template');
    template.innerHTML = html;
    DISALLOWED_TAGS.forEach((tagName) => {
        template.content.querySelectorAll(tagName).forEach((node) => node.remove());
    });

    template.content.querySelectorAll('*').forEach((node) => {
        Array.from(node.attributes).forEach((attribute) => {
            const attributeName = attribute.name.toLowerCase();
            if (attributeName.startsWith('on') || !ALLOWED_ATTRIBUTES.has(attributeName)) {
                node.removeAttribute(attribute.name);
                return;
            }

            if (attributeName === 'href' && !isSafeHref(attribute.value)) {
                node.removeAttribute(attribute.name);
            }
        });

        if (node.tagName === 'A' && node.getAttribute('href')) {
            node.setAttribute('target', '_blank');
            node.setAttribute('rel', 'noreferrer');
        }
    });

    return template.innerHTML;
}

// Convert saved text into editor-ready HTML while preserving legacy plain text.
export function normalizeRichTextHtml(text: string): string {
    const trimmedText = text.trim();
    if (!trimmedText) {
        return '';
    }

    if (RICH_TEXT_TAG_REGEX.test(text)) {
        return sanitizeRichTextHtml(text);
    }

    if (ENCODED_RICH_TEXT_TAG_REGEX.test(text)) {
        return sanitizeRichTextHtml(decodeHtmlEntities(text));
    }

    return escapeHtml(text).replace(/\n/g, '<br>');
}

// Extract readable plain text from HTML-rich content for validation and exports.
export function stripRichTextFormatting(text: string): string {
    if (!text.trim()) {
        return '';
    }

    if (typeof document === 'undefined') {
        return text.replace(/<[^>]+>/g, '').trim();
    }

    const container = document.createElement('div');
    container.innerHTML = normalizeRichTextHtml(text);
    return (container.innerText || container.textContent || '')
        .replace(/\u00a0/g, ' ')
        .trim();
}

// Use DOM traversal when innerText is unavailable so line breaks survive extraction.
function serializePlainTextNode(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent || '';
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
    }

    const element = node as HTMLElement;
    if (element.tagName === 'BR') {
        return '\n';
    }

    const childText = Array.from(element.childNodes).map(serializePlainTextNode).join('');
    if (BLOCK_TAGS.has(element.tagName)) {
        return `${childText}\n`;
    }

    return childText;
}

// Render the current HTML into fallback plain text for non-layout environments.
function serializePlainTextFallback(html: string): string {
    if (typeof document === 'undefined') {
        return html.replace(/<[^>]+>/g, '').trim();
    }

    const container = document.createElement('div');
    container.innerHTML = html;
    return Array.from(container.childNodes)
        .map(serializePlainTextNode)
        .join('')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

// Keep execCommand font size 3 from leaving redundant font tags behind.
function unwrapDefaultSizedText(editor: HTMLElement) {
    editor.querySelectorAll('font[size="3"]').forEach((fontNode) => {
        const parentNode = fontNode.parentNode;
        if (!parentNode) {
            return;
        }

        while (fontNode.firstChild) {
            parentNode.insertBefore(fontNode.firstChild, fontNode);
        }
        parentNode.removeChild(fontNode);
    });
}

export function RichTextEditor(props: RichTextEditorProps) {
    const {
        value,
        onChange,
        onBlur,
        disabled = false,
        placeholder = '',
        ariaLabel = 'Rich text editor',
        minHeight = '160px',
        maxHeight = '360px',
        unpadded = false,
        editorStyle,
        toolbarStyle,
    } = props;
    const editorRef = React.useRef<HTMLDivElement | null>(null);
    const [isFocused, setIsFocused] = React.useState(false);
    const normalizedValue = React.useMemo(() => normalizeRichTextHtml(value), [value]);

    React.useLayoutEffect(() => {
        // Sync external value changes without rebuilding the editor on every keystroke.
        const editor = editorRef.current;
        if (!editor || editor.innerHTML === normalizedValue) {
            return;
        }

        editor.innerHTML = normalizedValue;
    }, [normalizedValue]);

    const emitChange = React.useCallback(() => {
        // Store only sanitized editor HTML in parent component state.
        const editor = editorRef.current;
        if (!editor) {
            return '';
        }

        const nextHtml = sanitizeRichTextHtml(editor.innerHTML);
        onChange(nextHtml);
        return nextHtml;
    }, [onChange]);

    const applyEditorCommand = React.useCallback((command: string, commandValue?: string) => {
        // Delegate basic formatting to the browser's contenteditable command support.
        const editor = editorRef.current;
        if (!editor || disabled) {
            return;
        }

        editor.focus();
        document.execCommand(command, false, commandValue);
        emitChange();
    }, [disabled, emitChange]);

    const applyTextSize = React.useCallback((sizeCommandValue: '3' | '4' | '5') => {
        // Treat the default size as normal text instead of storing an explicit font wrapper.
        const editor = editorRef.current;
        if (!editor) {
            return;
        }

        applyEditorCommand('fontSize', sizeCommandValue);
        if (sizeCommandValue === '3') {
            unwrapDefaultSizedText(editor);
            emitChange();
        }
    }, [applyEditorCommand, emitChange]);

    const handleToolbarMouseDown = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        // Keep the current text selection available for the following toolbar click.
        event.preventDefault();
    }, []);

    const showPlaceholder = !isFocused && !stripRichTextFormatting(value);

    return (
        <div style={styles.editorShell}>
            <div style={{ ...styles.richTextToolbar, ...toolbarStyle }}>
                <button
                    type='button'
                    onMouseDown={handleToolbarMouseDown}
                    onClick={() => applyEditorCommand('bold')}
                    style={{ ...styles.toolbarButton, ...styles.toolbarButtonBold }}
                    disabled={disabled}
                    aria-label='Bold'
                    title='Bold'
                >
                    B
                </button>
                <button
                    type='button'
                    onMouseDown={handleToolbarMouseDown}
                    onClick={() => applyEditorCommand('italic')}
                    style={{ ...styles.toolbarButton, ...styles.toolbarButtonItalic }}
                    disabled={disabled}
                    aria-label='Italic'
                    title='Italic'
                >
                    I
                </button>
                <button
                    type='button'
                    onMouseDown={handleToolbarMouseDown}
                    onClick={() => applyEditorCommand('underline')}
                    style={{ ...styles.toolbarButton, ...styles.toolbarButtonUnderline }}
                    disabled={disabled}
                    aria-label='Underline'
                    title='Underline'
                >
                    U
                </button>
                <select
                    defaultValue='3'
                    onChange={(event) => applyTextSize(event.target.value as '3' | '4' | '5')}
                    style={styles.toolbarSelect}
                    disabled={disabled}
                    aria-label='Text size'
                    title='Text size'
                >
                    <option value='3'>Text: Small</option>
                    <option value='4'>Text: Medium</option>
                    <option value='5'>Text: Large</option>
                </select>
                <button
                    type='button'
                    onMouseDown={handleToolbarMouseDown}
                    onClick={() => applyEditorCommand('insertUnorderedList')}
                    style={styles.toolbarButton}
                    disabled={disabled}
                    aria-label='Bulleted list'
                    title='Bulleted list'
                >
                    {'\u2022 List'}
                </button>
                <button
                    type='button'
                    onMouseDown={handleToolbarMouseDown}
                    onClick={() => applyEditorCommand('insertOrderedList')}
                    style={styles.toolbarButton}
                    disabled={disabled}
                    aria-label='Numbered list'
                    title='Numbered list'
                >
                    1. List
                </button>
                <button
                    type='button'
                    onMouseDown={handleToolbarMouseDown}
                    onClick={() => {
                        const url = window.prompt('Enter the link URL');
                        if (url) {
                            applyEditorCommand('createLink', url);
                        }
                    }}
                    style={styles.toolbarButton}
                    disabled={disabled}
                    aria-label='Insert link'
                    title='Insert link'
                >
                    <img
                        src={require('./assets/icons/link.svg').default}
                        alt=''
                        style={styles.toolbarIconImage}
                    />
                </button>
                <button
                    type='button'
                    onMouseDown={handleToolbarMouseDown}
                    onClick={() => applyEditorCommand('removeFormat')}
                    style={styles.toolbarButton}
                    disabled={disabled}
                    aria-label='Clear formatting'
                    title='Clear formatting'
                >
                    <svg viewBox='0 0 24 24' style={styles.toolbarIcon} aria-hidden='true'>
                        <path
                            d='M7.4 6 12 10.6 16.6 6 18 7.4 13.4 12 18 16.6 16.6 18 12 13.4 7.4 18 6 16.6 10.6 12 6 7.4 7.4 6Z'
                            fill='currentColor'
                        />
                    </svg>
                </button>
            </div>
            <div style={styles.editorFrame}>
                {showPlaceholder && (
                    <div style={{ ...styles.placeholder, ...(unpadded ? { top: 0, left: 0 } : {}) }}>
                        {placeholder}
                    </div>
                )}
                <div
                    ref={editorRef}
                    // SMS has no visual insets, including margins inherited by pasted paragraphs.
                    className={unpadded ? 'rich-text-editor--unpadded' : undefined}
                    contentEditable={!disabled}
                    onInput={emitChange}
                    onBlur={() => {
                        setIsFocused(false);
                        const nextHtml = emitChange() || '';
                        onBlur?.(nextHtml);
                    }}
                    onFocus={() => setIsFocused(true)}
                    style={{
                        ...styles.richTextEditor,
                        ...(unpadded ? { padding: 0 } : {}),
                        minHeight,
                        maxHeight,
                        ...(disabled ? styles.richTextEditorDisabled : {}),
                        ...editorStyle,
                    }}
                    suppressContentEditableWarning
                    role='textbox'
                    aria-multiline='true'
                    aria-label={ariaLabel}
                />
            </div>
        </div>
    );
}

export function richTextHtmlToPlainText(text: string): string {
    // Public fallback keeps exports readable when innerText is unavailable.
    return serializePlainTextFallback(normalizeRichTextHtml(text));
}

const styles: Record<string, React.CSSProperties> = {
    editorShell: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%',
    },
    richTextToolbar: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        padding: '10px 12px',
        borderRadius: '8px',
        backgroundColor: Theme.Colours.tableHeader,
        width: '100%',
        boxSizing: 'border-box',
    },
    toolbarButton: {
        ...Theme.Fonts.body,
        marginLeft: '0px',
        padding: '8px 12px',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: 'transparent',
        color: Theme.Colours.text,
        cursor: 'pointer',
        minWidth: '44px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.15s ease',
    },
    toolbarButtonBold: {
        fontWeight: 'bold',
    },
    toolbarButtonItalic: {
        fontStyle: 'italic',
    },
    toolbarButtonUnderline: {
        textDecoration: 'underline',
    },
    toolbarSelect: {
        ...Theme.Fonts.body,
        marginLeft: '0px',
        padding: '8px 12px',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: 'transparent',
        color: Theme.Colours.text,
        cursor: 'pointer',
        minWidth: '132px',
    },
    toolbarIcon: {
        width: '18px',
        height: '18px',
        display: 'block',
    },
    toolbarIconImage: {
        width: '18px',
        height: '18px',
        display: 'block',
        objectFit: 'contain',
    },
    editorFrame: {
        position: 'relative',
        width: '100%',
    },
    placeholder: {
        ...Theme.Fonts.body,
        position: 'absolute',
        top: '12px',
        left: '16px',
        color: 'rgba(0, 0, 0, 0.42)',
        pointerEvents: 'none',
        lineHeight: 1.5,
    },
    richTextEditor: {
        ...Theme.Fonts.body,
        width: '100%',
        padding: '12px 16px',
        borderRadius: '8px',
        border: Theme.Colours.border,
        backgroundColor: Theme.Colours.background,
        color: Theme.Colours.text,
        boxSizing: 'border-box',
        overflowY: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        outline: 'none',
        lineHeight: 1.5,
    },
    richTextEditorDisabled: {
        cursor: 'not-allowed',
        opacity: 0.68,
    },
};
